import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { orphanMarketplaceAPI, startupMarketplaceAPI } from '../../utils/api';
import { ShellRail } from '../../shell/AppShell';
import { ChevronRightIcon } from '../../shell/icons';

/**
 * MarketplacesHome — the in-shell /marketplaces browse surface, rebuilt per
 * the shell design doctrine (.claude/SHELL-REDESIGN-PLAN.md + BRIEFS P3).
 * Logic carried from MarketplacesPage (list APIs, type toggle via ?type=);
 * presentation is shell-native. Detail/create/owner surfaces rebuild next.
 */

const asArray = (data, ...keys) => {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
};

const settingsOf = (l) => l.leagueSettings || l.settings || {};
const isSF = (l) => !!(settingsOf(l).isSuperFlex ?? l.superflex ?? l.isSuperFlex);
const tepOf = (l) => Number(settingsOf(l).tePremium ?? l.tep ?? 0);
const buyInOf = (l) => {
  const n = Number(l.buyIn ?? l.buy_in);
  return Number.isNaN(n) ? 0 : n;
};
const buyInLabel = (l) => {
  const b = buyInOf(l);
  return b ? `$${Math.round(b)}` : 'Free';
};
const paidThrough = (l) => {
  const years = Array.isArray(l.yearsPaid) ? l.yearsPaid : [];
  return years.length ? Math.max(...years.map(Number).filter((y) => !Number.isNaN(y))) : null;
};
const topPlayerNames = (l) =>
  (Array.isArray(l.topPlayers) ? l.topPlayers : [])
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.fullName || p?.full_name))
    .filter(Boolean)
    .slice(0, 3);

const listingMeta = (l) => {
  const s = settingsOf(l);
  const parts = [];
  if (s.numTeams) parts.push(`${s.numTeams} teams`);
  parts.push(isSF(l) ? 'SF' : '1QB');
  const ppr = Number(s.ppr ?? 0);
  if (ppr >= 1) parts.push('PPR');
  else if (ppr > 0) parts.push('Half PPR');
  if (tepOf(l) > 0) parts.push('TEP');
  return parts.join(' · ');
};

const FILTERS = ['All', 'SF', '1QB', 'TEP', 'Free'];
const matchesFilter = (l, f) => {
  if (f === 'SF') return isSF(l);
  if (f === '1QB') return !isSF(l);
  if (f === 'TEP') return tepOf(l) > 0;
  if (f === 'Free') return !buyInOf(l);
  return true;
};

const MarketplacesHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const type = searchParams.get('type') === 'startup' ? 'startup' : 'orphan';
  const filter = FILTERS.includes(searchParams.get('f')) ? searchParams.get('f') : 'All';

  const [orphans, setOrphans] = useState(null);
  const [startups, setStartups] = useState(null);
  const [appliedIds, setAppliedIds] = useState({ orphan: new Set(), startup: new Set() });
  const [myCounts, setMyCounts] = useState({ listings: 0, applications: 0 });

  useEffect(() => {
    let active = true;
    orphanMarketplaceAPI
      .list()
      .then((res) => { if (active) setOrphans(asArray(res.data, 'orphans', 'listings')); })
      .catch(() => { if (active) setOrphans([]); });
    startupMarketplaceAPI
      .list()
      .then((res) => { if (active) setStartups(asArray(res.data, 'startups', 'listings')); })
      .catch(() => { if (active) setStartups([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    Promise.allSettled([
      orphanMarketplaceAPI.getMyApplications(),
      startupMarketplaceAPI.getMyApplications(),
      orphanMarketplaceAPI.getMyListings(),
      startupMarketplaceAPI.getMyListings(),
    ]).then(([oa, sa, ol, sl]) => {
      if (!active) return;
      const ids = { orphan: new Set(), startup: new Set() };
      let applications = 0;
      if (oa.status === 'fulfilled') {
        asArray(oa.value.data, 'applications').forEach((a) => {
          applications += 1;
          const listing = a.orphanTeam || a.OrphanTeam || {};
          if (listing.id) ids.orphan.add(listing.id);
          else if (a.orphanTeamId) ids.orphan.add(a.orphanTeamId);
        });
      }
      if (sa.status === 'fulfilled') {
        asArray(sa.value.data, 'applications').forEach((a) => {
          applications += 1;
          const listing = a.startupListing || a.StartupListing || {};
          if (listing.id) ids.startup.add(listing.id);
          else if (a.startupListingId) ids.startup.add(a.startupListingId);
        });
      }
      const listings =
        (ol.status === 'fulfilled' ? asArray(ol.value.data, 'listings').length : 0) +
        (sl.status === 'fulfilled' ? asArray(sl.value.data, 'listings').length : 0);
      setAppliedIds(ids);
      setMyCounts({ listings, applications });
    });
    return () => { active = false; };
  }, [user]);

  const setParam = (key, value, defaultValue) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const listings = type === 'orphan' ? orphans : startups;
  const loading = listings === null;
  const filtered = useMemo(
    () => (listings || []).filter((l) => matchesFilter(l, filter)),
    [listings, filter]
  );

  const basePath = type === 'orphan' ? '/orphan-marketplace' : '/startup-marketplace';
  const newestThree = (orphans || []).slice(0, 3);

  return (
    <div className="ft-view">
      <Helmet>
        <title>Marketplaces — Fantasy Taco</title>
        <meta name="description" content="Find your next dynasty league. Orphan teams and startup leagues, with every applicant's dynasty resume attached." />
      </Helmet>

      <ShellRail>
        {user && (
          <div className="ft-railsec">
            <span className="ft-k">Your activity</span>
            <div className="ft-progressrow">
              <span>Applications out</span>
              <span className="ft-chip outline">{myCounts.applications}</span>
            </div>
            <div className="ft-progressrow">
              <span>Your listings</span>
              <span className="ft-chip outline">{myCounts.listings}</span>
            </div>
          </div>
        )}
        {newestThree.length > 0 && (
          <div className="ft-railsec">
            <span className="ft-k">Newest orphans</span>
            {newestThree.map((l) => (
              <button
                key={l.id}
                className="ft-railaction"
                onClick={() => navigate(`/orphan-marketplace/${l.id}`)}
              >
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.leagueName || l.teamName || 'Orphan team'}
                </span>
                <ChevronRightIcon />
              </button>
            ))}
          </div>
        )}
        <div className="ft-railsec">
          <span className="ft-k">Why list here</span>
          <p className="ft-sub" style={{ fontSize: 13.5, marginTop: 8 }}>
            Every applicant arrives with their dynasty resume attached — record, titles, trade history.
          </p>
        </div>
      </ShellRail>

      <span className="ft-k brand">Marketplaces</span>
      <h1 className="ft-hero">Find your next league.</h1>
      <p className="ft-sub">Orphans with history. Startups with a clean slate.</p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '26px 0 18px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: 'var(--ft-surface)', borderRadius: 'var(--ft-r-pill)', padding: 3 }}>
          {[['orphan', 'Orphan teams'], ['startup', 'Startups']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setParam('type', key, 'orphan')}
              style={{
                borderRadius: 'var(--ft-r-pill)', padding: '8px 22px', fontSize: 13.5, fontWeight: 600,
                background: type === key ? 'var(--ft-dark)' : 'transparent',
                color: type === key ? '#fff' : 'var(--ft-ink-3)',
                transition: 'all .2s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ft-pillrow">
          {FILTERS.map((f) => (
            <button
              key={f}
              className={`ft-fpill${filter === f ? ' on' : ''}`}
              onClick={() => setParam('f', f, 'All')}
            >
              {f}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button className="ft-btn sm" onClick={() => navigate(`${basePath}/list`)}>
          List a team
        </button>
      </div>

      {loading ? (
        <div className="ft-loading">Scanning the market…</div>
      ) : filtered.length === 0 ? (
        <p className="ft-sub" style={{ marginTop: 20 }}>
          {listings.length === 0
            ? `No ${type === 'orphan' ? 'orphan teams' : 'startup leagues'} listed right now.`
            : 'Nothing matches that filter.'}
        </p>
      ) : (
        <div className="ft-rowcards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {filtered.map((l) => {
            const applied = appliedIds[type].has(l.id);
            const players = topPlayerNames(l);
            const paid = paidThrough(l);
            return (
              <button key={l.id} className="ft-leaguecard" onClick={() => navigate(`${basePath}/${l.id}`)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span
                    className="ft-chip"
                    style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }}
                  >
                    {listingMeta(l)}
                  </span>
                  {l.isLister ? (
                    <span className="ft-chip brand" style={{ flex: 'none' }}>Your listing</span>
                  ) : applied ? (
                    <span className="ft-chip green" style={{ flex: 'none' }}>Applied</span>
                  ) : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  {l.leagueAvatarUrl ? (
                    <img
                      src={l.leagueAvatarUrl}
                      alt=""
                      style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', flex: 'none' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : null}
                  <div style={{ minWidth: 0 }}>
                    <div className="nm" style={{ marginBottom: 1 }}>{l.leagueName || l.teamName || 'League'}</div>
                    {l.teamName && l.leagueName ? <div className="tm" style={{ marginBottom: 0 }}>{l.teamName}</div> : null}
                  </div>
                </div>
                {l.description ? (
                  <div className="tm" style={{
                    marginBottom: 10, whiteSpace: 'normal', lineHeight: 1.45,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {l.description}
                  </div>
                ) : null}
                {players.length > 0 ? (
                  <div className="ft-pillrow" style={{ marginBottom: 10 }}>
                    {players.map((p) => <span key={p} className="ft-chip outline">{p}</span>)}
                  </div>
                ) : null}
                <div className="ft-pillrow">
                  <span className="ft-chip dark">{buyInLabel(l)}</span>
                  {paid ? <span className="ft-chip outline">Paid through {paid}</span> : null}
                  {l.escrowType ? <span className="ft-chip outline">{l.escrowType}</span> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MarketplacesHome;
